import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Form, InputNumber, message, Modal } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { useForm } from 'antd/lib/form/Form';
import {
	removePartFromWorkOrder,
	updateTotalPartsCost,
} from '../../thunks/parts_equipments_work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { isSupplierInventoryConfigEnabled } from '../common/SupplierInventoryConfigPermissionChecker';

const FORM_NAME = 'edit-wo-part-quantity-modal';

const EditWOPartQuantityModal: FC<any> = ({
	workOrderPart,
	removePartFromWO,
	updatePartCost,
	onSuccess,
	onCancel,
	match,
	companyConfig,
}): React.ReactElement => {
	const [form] = useForm();

	const [saving, setSaving] = useState(false);

	const existingQuantity = useMemo(
		() => nullSafeGetOrElse('partsQuantity', workOrderPart, 0),
		[workOrderPart]
	);

	const existingCost = useMemo(
		() => nullSafeGetOrElse('totalPartsCost', workOrderPart, 0),
		[workOrderPart]
	);

	const canEditCost = useMemo(
		() =>
			isSupplierInventoryConfigEnabled(companyConfig, 'allowWorkOrderPartsAndEquipmentCostEdit'),
		[companyConfig]
	);

	const workOrderId = useMemo(() => match.params.id, [match.params.id]);

	const onSubmit = useCallback(
		(values) => {
			const quantityChanged = values.quantity !== existingQuantity;
			const costChanged = canEditCost && values.totalPartsCost !== existingCost;

			if (!quantityChanged && !costChanged) {
				onCancel();
				return;
			}

			setSaving(true);

			const updateQuantity = () => {
				if (!quantityChanged) return Promise.resolve();
				const entity = {
					workOrderId,
					partId: workOrderPart.partId,
					...(nullSafeGet('locationId', workOrderPart) && {
						locationId: workOrderPart.locationId,
					}),
					...(nullSafeGet('partsPerStockLocationId', workOrderPart) && {
						partsPerStockLocationId: workOrderPart.partsPerStockLocationId,
					}),
					quantity: existingQuantity - values.quantity,
				};
				return removePartFromWO(entity);
			};

			const updateCost = () => {
				if (!costChanged) return Promise.resolve();
				return updatePartCost({
					id: workOrderPart.id,
					totalPartsCost: values.totalPartsCost,
				});
			};

			updateQuantity()
				.then(() => updateCost())
				.then(() => onSuccess())
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[
			existingQuantity,
			existingCost,
			canEditCost,
			onSuccess,
			onCancel,
			workOrderPart,
			removePartFromWO,
			updatePartCost,
			workOrderId,
		]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={'Update Part Quantity'}
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					Update
				</Button>,
			]}
		>
			<Form form={form} id={FORM_NAME} layout="vertical" requiredMark={false} onFinish={onSubmit}>
				<Form.Item
					name="quantity"
					label="Quantity"
					initialValue={existingQuantity}
					rules={[
						{
							type: 'number',
							min: 1,
							message: `You must enter at least one`,
						},
						{
							type: 'number',
							max: existingQuantity,
							message: `You can't enter more than ${existingQuantity}`,
						},
						{
							required: true,
						},
					]}
				>
					<InputNumber style={{ width: '100%' }} min="1" />
				</Form.Item>
				{canEditCost && (
					<Form.Item
						name="totalPartsCost"
						label="Cost"
						initialValue={existingCost}
						rules={[
							{
								validator: (_, value) => {
									if (value !== null && value !== undefined && value < 0) {
										return Promise.reject('Cost must be non-negative');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<InputNumber style={{ width: '100%' }} min={0} precision={2} />
					</Form.Item>
				)}
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	removePartFromWO: (partData) => dispatch(removePartFromWorkOrder(ROLE_TYPES.SUPPLIER)(partData)),
	updatePartCost: (entity) => dispatch(updateTotalPartsCost(ROLE_TYPES.SUPPLIER)(entity)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(EditWOPartQuantityModal)
);
